<template>
  <div>
    <primary-confirm-dialog
      v-model="showDialog"
      :title="dialogTitle"
      :cancel-text="cancelText"
      :hide-confirm-btn="cancelRestricted"
      v-bind="$attrs"
      persistent
    >
      <template #subtitle>
        <!-- Кассовый платёж: сначала сумма и способ, потом что произойдёт после возврата -->
        <div v-if="showPaymentSummary" class="cancel-order-dialog_payment">
          <div class="cancel-order-dialog_payment-summary">
            {{ paymentSummary }}
          </div>
          <div>{{ dialogSubtitle }}</div>
        </div>
        <div v-else class="d-flex align-center justify-center">
          <v-icon
            v-if="dialogSubtitle"
            :color="cancelRestricted ? '#d32f2f' : '#000'"
            :size="cancelRestricted ? 36 : undefined"
            class="mr-2"
          >
            {{ cancelRestricted ? 'mdi-alert' : 'mdi-alert-circle-outline' }}
          </v-icon>
          <span>{{ dialogSubtitle }}</span>
        </div>
      </template>
      <!-- Единственная кнопка в режиме запрета: во всю ширину карточки -->
      <template v-if="cancelRestricted" #cancel-btn>
        <large-btn
          class="primary-confirm-dialog_btn primary-confirm-dialog_btn__cancel cancel-restricted-back-btn"
          color="#f1f3f6"
          height="64px"
          :uppercase="false"
          block
          @click="showDialog = false"
        >
          {{ cancelText }}
        </large-btn>
      </template>
      <template v-if="!cancelRestricted" #confirm-btn>
        <large-btn
          class="primary-confirm-dialog_btn primary-confirm-dialog_btn__confirm confirm-cancel-btn"
          color="red"
          height="64px"
          :uppercase="false"
          block
          :loading="loading"
          @click="confirm"
        >
          <font-awesome-icon :icon="hasCashierPayment ? 'undo' : 'times'" class="mr-1" />
          {{ confirmButtonText }}
        </large-btn>
      </template>
    </primary-confirm-dialog>
    <cancel-payment-dialog
      v-model="showRefundDialog"
      :order="order"
      cancel-order
      @canceled="onPaymentCanceled"
    />
  </div>
</template>

<script>
  import LargeBtn from '@/components/ui/buttons/LargeBtn.vue'
  import PrimaryConfirmDialog from '@/components/ui/dialogs/PrimaryConfirmDialog.vue'
  import CancelPaymentDialog from './payment/CancelPaymentDialog.vue'
  import { isCashierPayment } from '@/models/payment/isCashierPayment'
  import PaymentType from '@/models/enums/PaymentType'
  import { getCurrency } from '@/util/money'
  import Role from '@/models/enums/Roles'

  export default {
    name: 'CancelOrderDialog',
    components: { PrimaryConfirmDialog, LargeBtn, CancelPaymentDialog },
    // корень — обёрточный div (диалог возврата рендерится рядом с primary-confirm-dialog): $attrs пробрасываем вручную, не на div
    inheritAttrs: false,
    props: {
      value: Boolean,
      order: Object,
      loading: {
        type: Boolean,
        default: false,
      },
      confirmText: {
        type: String,
        default: 'Отменить заказ',
      },
      cancelText: {
        type: String,
        default: 'Вернуться назад',
      },
    },
    data () {
      return {
        showRefundDialog: false,
      }
    },
    computed: {
      // Запрет стоит на сотруднике (users.can_cancel_orders); суперадмина бэкенд не ограничивает
      cancelRestricted () {
        return !this.userIs(Role.SuperAdmin) &&
          this.$store.state.auth.user?.canCancelOrders === false
      },
      dialogTitle () {
        if (this.cancelRestricted) {
          return 'Отмена заказа невозможна'
        }
        if (this.hasCashierPayment) {
          return `Отмена заказа №${this.order.code}`
        }
        return `Вы уверены что хотите отменить заказ №${this.order.code}?`
      },
      hasCashierPayment () {
        return isCashierPayment(this.order)
      },
      confirmButtonText () {
        return this.hasCashierPayment ? 'Вернуть оплату' : this.confirmText
      },
      showPaymentSummary () {
        return this.hasCashierPayment && !this.cancelRestricted
      },
      paymentSummary () {
        const payment = this.order?.payment
        if (!payment) return null

        const deliveryType = this.order.deliveryType || this.order.basket?.deliveryType
        const typeName = PaymentType.getName(payment.type, deliveryType, payment.mode).toLowerCase()

        return `Заказ оплачен ${typeName}: ${payment.total}${getCurrency()}`
      },
      dialogSubtitle () {
        if (this.cancelRestricted) {
          return 'Отмена заказа ограничена администрацией ресторана, обратитесь к управляющему'
        }
        if (this.hasCashierPayment) {
          return 'Сначала верните деньги клиенту, после возврата заказ отменится сам'
        }
        return null
      },
      showDialog: {
        get () {
          return this.value
        },
        set (value) {
          this.$emit('input', value)
        },
      },
    },
    watch: {
      // «Вернуться назад» закрывает только внешний диалог: сбрасываем вложенный, иначе он всплывёт при следующем открытии
      value (opened) {
        if (!opened) {
          this.showRefundDialog = false
        }
      },
    },
    methods: {
      confirm () {
        if (this.hasCashierPayment) {
          this.showRefundDialog = true
          return
        }
        this.$emit('cancel')
      },
      // заказ отменил бэк в хуке возврата (или не смог — диалог уже показал ошибку); родителю остаётся закрыться/уйти
      onPaymentCanceled (result) {
        this.showRefundDialog = false
        this.showDialog = false
        this.$emit('canceled', result)
      },
    },
  }
</script>

<style scoped lang="less">
.cancel-restricted-back-btn {
  width: 100% !important;
  max-width: none !important;
  min-width: 0 !important;
}

.cancel-order-dialog_payment-summary {
  font-weight: 500;
  color: #111;
  margin-bottom: 8px;
}

// v-btn ставит nowrap: длинная подпись вылезала бы за кнопку на соседнюю. Разрешаем перенос внутри тех же 64px
.confirm-cancel-btn {
  color: #fff !important;
  padding: 0 20px !important;
  white-space: normal;

  ::v-deep .v-btn__content {
    line-height: 1.2;
  }
}
</style>
